import { type FC } from "react";
import { FormProvider, SubmitHandler, useController, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import {
  CODE_LANG_OPTIONS,
  CodeKernelLanguage,
  DEFAULT_CODE_KERNEL_LANG,
} from "../../shared/worksheets/code-kernels";
import { toastSuccessMessage } from "../../utils/alerts";
import { BUTTON_EVENT_CATEGORY, GENERAL_EVENT_CATEGORY, trackEvent } from "../../utils/amplitude";

import { LabeledCheckbox } from "./LabeledCheckbox";
import { LateSubmissionIntervalDropdown } from "./LateSubmissionIntervalDropdown";
import { SubmitOrCancelButtons } from "./SubmitOrCancelButtons";
import {
  formatDaysToDjangoDurationField,
  formatDjangoDurationFieldToDaysCount,
} from "./due-date-form-helpers";

import { SectionType } from "components/server-types";
import { LANGUAGE_LABELS, SupportedLanguage, t } from "i18n/i18n";
import { DropdownMenu } from "mds/components/DropdownMenu";
import { Tooltip } from "mds/components/Tooltip";
import { CircleInfoIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectDesignatedSection } from "store/selectors";
import { PROJECTOR_ZOOM_ITEMS } from "utils/presentation";
import { isLinkedSection } from "utils/publishing";

type FormValues = {
  allowLateSubmissions: boolean;
  anonymous_grading_enabled: boolean;
  code_blocks_default_language: CodeKernelLanguage;
  code_blocks_enabled: boolean;
  section_language: SectionType["section_language"];
  daysLate: number;
  external_section_id: string;
  hide_static_blocks_on_student_response_page: boolean;
  projector_zoom: number;
  should_lock_pages_at_due_date: boolean;
  student_ai_assistant_enabled: boolean;
};

interface SectionSettingsGeneralFormProps {
  className?: string;
  onClose: () => void;
}

export const SectionSettingsGeneralForm: FC<SectionSettingsGeneralFormProps> = ({
  className,
  onClose,
}) => {
  const currentSection = useAppSelector(selectDesignatedSection);

  const lateInterval = currentSection?.default_late_submission_interval;
  const daysLate = formatDjangoDurationFieldToDaysCount(lateInterval ?? null);
  // If daysLate > 0 (or Infinity), late submissions are allowed
  const allowLateSubmissions = daysLate > 0;

  const form = useForm<FormValues>({
    defaultValues: {
      allowLateSubmissions,
      anonymous_grading_enabled: currentSection?.anonymous_grading_enabled,
      code_blocks_default_language:
        currentSection?.code_blocks_default_language || DEFAULT_CODE_KERNEL_LANG,
      code_blocks_enabled: currentSection?.code_blocks_enabled,
      section_language: currentSection?.section_language,
      daysLate: allowLateSubmissions ? daysLate : 1,
      hide_static_blocks_on_student_response_page:
        currentSection?.hide_static_blocks_on_student_response_page,
      projector_zoom: currentSection?.projector_zoom,
      should_lock_pages_at_due_date: currentSection?.should_lock_pages_at_due_date,
      student_ai_assistant_enabled: currentSection?.student_ai_assistant_enabled,
    },
  });

  const { field: daysLateField } = useController({ name: "daysLate", control: form.control });

  if (!currentSection) {
    return null;
  }

  const isLinked = isLinkedSection(currentSection);

  const codeBlocksDefaultLanguage = form.watch("code_blocks_default_language");
  const codeBlocksEnabled = form.watch("code_blocks_enabled");
  const projectorZoom = form.watch("projector_zoom");
  const sectionLanguage = form.watch("section_language");
  const formAllowLateSubmissions = form.watch("allowLateSubmissions");

  const unspecifiedLanguageId = "unspecified";
  const languageOptions = Object.entries(LANGUAGE_LABELS)
    .map(([id, label]) => ({
      id,
      title: label,
    }))
    .concat([{ id: unspecifiedLanguageId, title: t("settings.section_language_unspecified") }]);

  const onSubmit: SubmitHandler<FormValues> = (formData) => {
    if (isLinked) {
      return;
    }

    trackEvent("Section Settings - Save changes", BUTTON_EVENT_CATEGORY);
    const zoomHasChanged = formData.projector_zoom !== currentSection.projector_zoom;

    const default_late_submission_interval = formatDaysToDjangoDurationField(
      formData.allowLateSubmissions ? formData.daysLate : 0,
    );

    return storeApi.sections
      .partial_update({
        id: currentSection.id,
        anonymous_grading_enabled: formData.anonymous_grading_enabled,
        code_blocks_default_language: formData.code_blocks_default_language,
        code_blocks_enabled: formData.code_blocks_enabled,
        section_language: formData.section_language,
        default_late_submission_interval,
        hide_static_blocks_on_student_response_page:
          formData.hide_static_blocks_on_student_response_page,
        projector_zoom: formData.projector_zoom,
        should_lock_pages_at_due_date: formData.should_lock_pages_at_due_date,
        student_ai_assistant_enabled: formData.student_ai_assistant_enabled,
      })
      .then(() => {
        toastSuccessMessage(t("success.toasts.updated_section_settings"));

        if (zoomHasChanged) {
          trackEvent("Section Settings - Projector view zoom level", {
            ...GENERAL_EVENT_CATEGORY,
            zoom: formData.projector_zoom,
          });
        }

        onClose();
      });
  };

  return (
    <FormProvider {...form}>
      <form
        className={twMerge(
          "flex flex-col gap-2 text-sm font-medium leading-[1.3125rem] text-black-tint-10",
          className,
        )}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <label className="flex items-center justify-between">
          <span>{t("settings.section.section_language")}</span>
          <input type="hidden" {...form.register("section_language")} />
          <div className="inline-block">
            <DropdownMenu
              disabled={isLinked}
              eventName="Course language changed"
              eventPropertyKey="newLanguage"
              items={languageOptions}
              selectedValue={sectionLanguage || "unspecified"}
              onSelectionChange={({ id }) =>
                form.setValue("section_language", id as SupportedLanguage)
              }
            />
          </div>
        </label>
        <span className="text-xs text-black-tint-55">{t("settings.section_language_tooltip")}</span>

        <LabeledCheckbox
          eventName="Section Settings - Student AI chat toggle"
          registration={form.register("student_ai_assistant_enabled", { disabled: isLinked })}
        >
          {t("settings.section.student_ai_assistant_enabled")}
        </LabeledCheckbox>

        <LabeledCheckbox
          eventName="Section Settings - Anonymous grading toggle"
          registration={form.register("anonymous_grading_enabled", { disabled: isLinked })}
        >
          {t("settings.section.anonymous_grading_enabled")}
        </LabeledCheckbox>

        <Tooltip text={t("settings.hiding_static_blocks_tooltip")}>
          <LabeledCheckbox
            eventName="Section Settings - Non-interactive blocks toggle"
            registration={form.register("hide_static_blocks_on_student_response_page", {
              disabled: isLinked,
            })}
          >
            {t("settings.section.hide_static_blocks_on_student_response_page")}
          </LabeledCheckbox>
        </Tooltip>

        <LabeledCheckbox
          eventName="Section Settings - Code blocks toggle"
          registration={form.register("code_blocks_enabled", { disabled: isLinked })}
        >
          {t("settings.section.code_blocks_enabled")}
        </LabeledCheckbox>

        <LabeledCheckbox
          eventName="Section Settings - Lock pages at due date toggle"
          registration={form.register("should_lock_pages_at_due_date", { disabled: isLinked })}
        >
          {t("settings.section.lock_pages_at_due_date")}
        </LabeledCheckbox>

        <div className="flex items-center gap-1">
          <LabeledCheckbox
            eventName="Section Settings - Allow late submissions toggle"
            registration={form.register("allowLateSubmissions", { disabled: isLinked })}
          >
            {t("settings.section.allow_late_submissions")}
          </LabeledCheckbox>
          <Tooltip text={t("settings.section.allow_late_submissions_tooltip")}>
            <CircleInfoIcon className="text-black-tint-40" size="s" />
          </Tooltip>
        </div>

        <label className="ml-5 flex items-center gap-2">
          <span
            className={twMerge(
              formAllowLateSubmissions ? "text-black-tint-20" : "text-black-tint-55",
            )}
          >
            {t("settings.section.grace_period")}
          </span>
          <LateSubmissionIntervalDropdown
            disabled={!formAllowLateSubmissions || isLinked}
            value={daysLateField.value}
            onChange={daysLateField.onChange}
          />
        </label>

        <label className="flex items-center justify-between">
          <span>{t("settings.default_code_block_language")}</span>
          <input type="hidden" {...form.register("code_blocks_default_language")} />
          <div className="inline-block">
            <DropdownMenu
              disabled={!codeBlocksEnabled || isLinked}
              eventName="Section Settings - Programming language changed"
              eventPropertyKey="newLanguage"
              items={CODE_LANG_OPTIONS}
              selectedValue={codeBlocksDefaultLanguage}
              onSelectionChange={({ id }) => form.setValue("code_blocks_default_language", id)}
            />
          </div>
        </label>

        <label className="flex items-center justify-between">
          <span>{t("settings.projector_zoom")}</span>
          <input type="hidden" {...form.register("projector_zoom")} />
          <div className="inline-block">
            <DropdownMenu
              disabled={isLinked}
              eventName="Section Settings - Projector zoom changed"
              eventPropertyKey="zoom"
              items={PROJECTOR_ZOOM_ITEMS}
              selectedValue={projectorZoom.toString()}
              onSelectionChange={({ id }) => form.setValue("projector_zoom", parseInt(id, 10))}
            />
          </div>
        </label>

        <SubmitOrCancelButtons className="pt-8" disableSubmit={isLinked} onCancel={onClose} />
      </form>
    </FormProvider>
  );
};
